export const DYNAMIC_SNAPSHOT_PROVIDERS = [
  'layerStats',
  'incidents',
  'orgEvents',
  'actions',
] as const;

export type DynamicSectionDataMode = 'snapshot' | 'live';

export type DynamicSnapshotSectionLike = {
  id?: string | null;
  section_id?: string | null;
  type?: string | null;
  automation?: { provider?: string | null } | null;
  output_format_structured?: {
    automation?: { provider?: string | null };
  } | null;
  frozen_version_id?: string | null;
  section_versions?: Array<{
    version_id?: string | null;
    id?: string | null;
    data_string?: string | null;
    sitrep_section_text?: string | null;
    status?: string | null;
    timestamp?: string | null;
  }> | null;
};

export function isDynamicSnapshotSection(
  section: DynamicSnapshotSectionLike | null | undefined
): boolean {
  if (section?.type !== 'automated') return false;
  const provider =
    section.automation?.provider ??
    section.output_format_structured?.automation?.provider;
  return (
    provider === 'layerStats' ||
    provider === 'incidents' ||
    provider === 'orgEvents' ||
    provider === 'actions'
  );
}

export function showsDynamicSnapshotToggle(
  approvalStatus?: string | null,
  sections?: DynamicSnapshotSectionLike[] | null
): boolean {
  return (
    (approvalStatus === 'PENDING' || approvalStatus === 'APPROVED') &&
    sitrepHasDynamicSnapshotSections(sections)
  );
}

export function dynamicSnapshotToggleLabels(approvalStatus?: string | null): {
  snapshot: string;
  live: string;
} {
  return {
    snapshot:
      approvalStatus === 'PENDING' ? 'Submitted Snapshot' : 'Approved Snapshot',
    live: 'Live Data',
  };
}

export function dynamicSnapshotToggleHints(approvalStatus?: string | null): {
  snapshot: string;
  live: string;
} {
  return {
    snapshot:
      approvalStatus === 'PENDING'
        ? 'The data saved when this report was submitted for review. It stays as it was.'
        : 'The data saved when this report was approved. It stays as it was.',
    live: 'Current data. Changes here do not replace the snapshot.',
  };
}

export type GeoSnapshotFeature = {
  attributes?: Record<string, unknown>;
  geometry?: unknown;
};

export type GeoSnapshotLayer = {
  id: string;
  name: string;
  features: GeoSnapshotFeature[];
  /** ArcGIS drawingInfo.renderer captured with the features. */
  renderer?: Record<string, unknown>;
};

export function parseGeoSnapshot(
  dataString?: string | null
): GeoSnapshotLayer[] {
  if (!dataString?.trim()) return [];
  try {
    const parsed = JSON.parse(dataString) as { geoSnapshot?: unknown };
    if (!Array.isArray(parsed?.geoSnapshot)) return [];
    return parsed.geoSnapshot.flatMap((layer): GeoSnapshotLayer[] => {
      if (!layer || typeof layer !== 'object') return [];
      const id = (layer as { id?: unknown }).id;
      const name = (layer as { name?: unknown }).name;
      const features = (layer as { features?: unknown }).features;
      const renderer = (layer as { renderer?: unknown }).renderer;
      if (
        typeof id !== 'string' ||
        typeof name !== 'string' ||
        !Array.isArray(features)
      ) {
        return [];
      }
      return [
        {
          id,
          name,
          ...(renderer &&
          typeof renderer === 'object' &&
          !Array.isArray(renderer)
            ? { renderer: renderer as Record<string, unknown> }
            : {}),
          features: features.flatMap((feature): GeoSnapshotFeature[] => {
            if (!feature || typeof feature !== 'object') return [];
            const geometry = (feature as { geometry?: unknown }).geometry;
            if (!geometry || typeof geometry !== 'object') return [];
            const attributes = (feature as { attributes?: unknown }).attributes;
            return [
              {
                geometry,
                ...(attributes && typeof attributes === 'object'
                  ? { attributes: attributes as Record<string, unknown> }
                  : {}),
              },
            ];
          }),
        },
      ];
    });
  } catch {
    return [];
  }
}

export function geoSnapshotHasFeatures(layers: GeoSnapshotLayer[]): boolean {
  return layers.some((layer) => layer.features.length > 0);
}

/** True when a frozen analytics version stored map features. */
export function sectionsHaveAnalyticsGeoSnapshot(
  sections?: DynamicSnapshotSectionLike[] | null
): boolean {
  return (sections ?? []).some((section) => {
    const provider =
      section.automation?.provider ??
      section.output_format_structured?.automation?.provider;
    if (section.type !== 'automated' || provider !== 'layerStats') return false;
    const version = snapshotVersion(section);
    return geoSnapshotHasFeatures(
      parseGeoSnapshot(version?.data_string ?? version?.sitrep_section_text)
    );
  });
}

/**
 * Hints for the analytics snapshot tabs. The tasks toggle keeps
 * dynamicSnapshotToggleHints.
 */
export function analyticsSnapshotToggleHints(
  approvalStatus?: string | null,
  hasGeoSnapshot = false
): { snapshot: string; live: string } {
  const submitted = approvalStatus === 'PENDING';
  const when = submitted ? 'submitted for review' : 'approved';
  const live =
    'Current analytics and the current map layer. Changes do not replace the snapshot.';
  if (hasGeoSnapshot) {
    return {
      snapshot: `The analytics and the map features saved when this report was ${when} stay as they were.`,
      live,
    };
  }
  const moment = submitted ? 'submission' : 'approval';
  return {
    snapshot: `The analytics saved when this report was ${when} stay as they were. The map still shows the current layer, not the features from ${moment}.`,
    live,
  };
}

export function sitrepHasDynamicSnapshotSections(
  sections?: DynamicSnapshotSectionLike[] | null
): boolean {
  return (sections ?? []).some((section) => isDynamicSnapshotSection(section));
}

/** True when this heading is the Analytics (or other live-data) section. */
export function sectionShowsSnapshotTabs(
  section: DynamicSnapshotSectionLike | null | undefined,
  childSections?: DynamicSnapshotSectionLike[] | null
): boolean {
  if (isDynamicSnapshotSection(section)) return true;
  return (childSections ?? []).some((child) => isDynamicSnapshotSection(child));
}

/**
 * The snapshot/live control belongs on the section that shows the data.
 * A category whose children are those sections does not host a second control.
 */
export function sectionRendersSnapshotToggle(
  section: DynamicSnapshotSectionLike | null | undefined,
  childSections?: DynamicSnapshotSectionLike[] | null
): boolean {
  if (!isDynamicSnapshotSection(section)) return false;
  return !(childSections ?? []).some((child) =>
    isDynamicSnapshotSection(child)
  );
}

export function snapshotVersion<
  T extends {
    version_id?: string | null;
    id?: string | null;
  },
>(section: {
  frozen_version_id?: string | null;
  section_versions?: T[] | null;
}): T | null {
  const versions = section.section_versions ?? [];
  const frozenId = section.frozen_version_id;
  if (frozenId) {
    const frozen = versions.find(
      (version) => version.version_id === frozenId || version.id === frozenId
    );
    if (frozen) return frozen;
  }
  return versions[0] ?? null;
}

export function parseOpordTasksSnapshot(
  dataString?: string | null
): unknown[] | null {
  if (!dataString?.trim()) return null;
  try {
    const parsed = JSON.parse(dataString) as {
      opordTasksSnapshot?: unknown;
      actions?: unknown;
    };
    if (!parsed?.opordTasksSnapshot || !Array.isArray(parsed.actions)) {
      return null;
    }
    return parsed.actions;
  } catch {
    return null;
  }
}

export function resolveEntityListLive<T>(
  live: T | undefined,
  useLiveEnrichment: boolean
): T | undefined {
  return useLiveEnrichment ? live : undefined;
}
